import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getPostBySlug, formatDate } from '../data/blogPosts';
import Seo from '../components/Seo';
import { buildBlogPostingSchema } from '../data/structuredData';

// Simple helper to set document head tags for SEO / social sharing.
// For full SSR-level SEO you'd want react-helmet-async or move to Next.js,
// but this gives crawlers and link previewers the right signals at render time.

const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const handleCopyLink = useCallback(async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, []);

  // 404 state for invalid slugs
  if (!post) {
    return (
      <div className="min-h-screen bg-white text-black">
      <Seo title="Post not found" path="/blog" noindex />
        <Header />
        <main className="pt-32 pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              404
            </p>
            <h1 className="text-4xl md:text-5xl font-black mb-6">
              Post not found
            </h1>
            <p className="text-gray-600 text-lg mb-10">
              The post you're looking for doesn't exist or may have been moved.
            </p>
            <button
              onClick={() => navigate('/blog')}
              className="bg-black text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors"
            >
              Back to Blog
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const paragraphs = post.body.split('\n\n');

  return (
    <div className="min-h-screen bg-white text-black">
    <Seo
  title={post.title}
  description={post.excerpt}
  path={`/blog/${post.id}`}
  type="article"
  jsonLd={buildBlogPostingSchema(post)}
/>
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Back nav */}
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-black transition-colors mb-8 group"
          >
            <span className="group-hover:-translate-x-1 transition-transform duration-200">
              ←
            </span>
            <span className="text-sm font-medium uppercase tracking-wider">
              Back to all posts
            </span>
          </Link>

          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                {post.category}
              </span>
              <span className="text-gray-300">|</span>
              <span className="text-xs text-gray-400">
                {`${post.readTime} min read`}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black leading-tight text-black mb-4">
              {post.title}
            </h1>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <span className="font-medium text-black">{post.author}</span>
                <span>·</span>
                <span>{formatDate(post.date)}</span>
              </div>
              <button
                onClick={handleCopyLink}
                className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-black border border-gray-200 hover:border-black px-3 py-1.5 transition-colors"
                aria-label="Copy link to this post"
              >
                {copied ? (
                  <>
                    <span>✓</span>
                    <span>Copied</span>
                  </>
                ) : (
                  <>
                    <span>🔗</span>
                    <span>Copy link</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="w-16 h-1 bg-black mb-10" />

          {/* Body */}
          <div className="max-w-none">
            {paragraphs.map((para, i) => {
              if (/^\d+\./.test(para.trim())) {
                return (
                  <p
                    key={i}
                    className="text-gray-700 text-lg leading-relaxed mb-4 pl-4 border-l-2 border-gray-200"
                  >
                    {para}
                  </p>
                );
              }
              if (para.trim().startsWith('•')) {
                const items = para.split('\n').filter(Boolean);
                return (
                  <div key={i} className="mb-6 space-y-2">
                    {items.map((item, j) => (
                      <p
                        key={j}
                        className="text-gray-700 text-lg leading-relaxed pl-4 border-l-2 border-gray-200"
                      >
                        {item}
                      </p>
                    ))}
                  </div>
                );
              }
              return (
                <p
                  key={i}
                  className="text-gray-700 text-lg leading-relaxed mb-6"
                >
                  {para}
                </p>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-16 p-8 bg-gray-50 border border-gray-200">
            <p className="text-lg font-bold text-black mb-2">
              Want to talk about this?
            </p>
            <p className="text-gray-600 mb-4">
              {'Reach out at '}
              <a
                href="mailto:dan@tarelabs.com"
                className="font-medium text-black hover:underline"
              >
                dan@tarelabs.com
              </a>
              {' or '}
              <a
                href="https://calendly.com/dan-tarelabs/new-meeting"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-black hover:underline"
              >
                book a free consultation
              </a>
              .
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
